import {
  SORT_OPTIONS,
  type CareType,
  type FilterState,
  type SortBy,
} from '@centivo/provider-directory-components'

/**
 * The slice of results-page UI state we want to survive leaving the page (e.g.
 * opening a provider/institution profile and coming back). It is intentionally
 * scoped to a single search: the store is keyed by the search query string, so
 * running a brand-new search starts from defaults while returning to the same
 * search restores the filters/toolbar the user had applied.
 */
export interface ResultsUiState {
  filters?: Partial<FilterState>
  careType?: CareType
  sortBy?: SortBy
}

const STORAGE_PREFIX = 'oe:results-ui-state:'

const storageKey = (searchKey: string): string =>
  `${STORAGE_PREFIX}${searchKey}`

const isKnownSortBy = (value: unknown): value is SortBy =>
  SORT_OPTIONS.some(option => option.value === value)

export const loadResultsUiState = (
  searchKey: string
): ResultsUiState | undefined => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(searchKey))
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as ResultsUiState
    if (parsed.sortBy !== undefined && !isKnownSortBy(parsed.sortBy)) {
      return { ...parsed, sortBy: undefined }
    }
    return parsed
  } catch {
    return undefined
  }
}

/**
 * Merges the given patch into any existing snapshot for this search and
 * persists it, so filter changes and toolbar changes accumulate independently.
 */
export const saveResultsUiState = (
  searchKey: string,
  patch: ResultsUiState
): void => {
  try {
    const current = loadResultsUiState(searchKey) ?? {}
    const next: ResultsUiState = { ...current, ...patch }
    window.sessionStorage.setItem(storageKey(searchKey), JSON.stringify(next))
  } catch {
    // Storage can be unavailable (private mode / quota); persistence is a
    // best-effort enhancement, so a failure here is safe to ignore.
  }
}
